// Who publishes each feed, and how their card on the sources page dresses.
//
// The diagnostics payload knows a feed's upstream host; it does not know that
// five Traffic Scotland layers are one organisation, or what that organisation
// looks like. That grouping is editorial, like SOURCE_NOTES next door, so it
// lives here rather than on the wire.

/** Feed id → the organisation whose card it belongs on. */
export const PUBLISHER: Record<string, string> = {
  air: 'Defra UK-AIR',
  aircraft: 'adsb.lol',
  airport: 'adsb.lol',
  buses: 'Ember',
  vessels: 'aisstream.io',
  overflows: 'Scottish Water',
  ferries: 'aisstream.io',
  rail: 'Rail Delivery Group',
  'rail-positions': 'Rail Delivery Group',
  'bus-stops': 'DfT BODS',
  taxis: 'NaPTAN (DfT)',
  lez: 'Spatial Hub (Improvement Service)',
  power: 'SSEN',
  'power-spen': 'SP Energy Networks',
  voi: 'Voi',
  incidents: 'Transport Scotland',
  roadworks: 'Transport Scotland',
  'roadworks-future': 'Transport Scotland',
  vms: 'Transport Scotland',
  'journey-times': 'Transport Scotland',
  cameras: 'Transport Scotland',
  'local-roadworks': 'Scottish Road Works Commissioner',
  rivers: 'SEPA',
  floods: 'SEPA',
  waves: 'Cefas WaveNet',
  tides: 'UK Hydrographic Office',
  fuel: 'Fuel Finder (gov.uk)',
  weather: 'Met Office',
  warnings: 'Met Office',
  water: 'Scottish Water',
  festivals: 'Edinburgh Festivals',
};

/**
 * How each logo file fits its box. The files are not uniform — some are
 * wordmark-shaped, some are wordmarks lost on a square canvas, one is a square
 * emblem that must never be cropped, and one carries its own background:
 * - wide:   white plate, contain — already wordmark-shaped
 * - crop:   white plate, cover — square canvas, wordmark in the middle band
 * - emblem: white plate, sized by height, never cropped
 * - bleed:  no plate, no padding — the file brings its own background
 */
export type LogoFit = 'wide' | 'crop' | 'emblem' | 'bleed';

export const LOGOS: Record<string, { src: string; fit: LogoFit }> = {
  'Transport Scotland': { src: '/logo-ts-blue.svg', fit: 'wide' },
  'aisstream.io': { src: '/logos/ais-stream.png', fit: 'crop' },
  SEPA: { src: '/logos/sepa.png', fit: 'wide' },
  Ember: { src: '/logos/ember.png', fit: 'crop' },
  'Met Office': { src: '/logos/met-office.svg', fit: 'emblem' },
  Voi: { src: '/logos/voi.svg', fit: 'wide' },
  'Cefas WaveNet': { src: '/logos/cefas.png', fit: 'wide' },
  SSEN: { src: '/logos/ssen.png', fit: 'wide' },
  // Wordmark-shaped: the glass sits left of "Scottish Water" on one line, so
  // it fits the box without cropping. Serves both the overflow layer and the
  // pending water one, which share a publisher.
  'Scottish Water': { src: '/logos/scottish-water.png', fit: 'wide' },
  'SP Energy Networks': { src: '/logos/scottish-power.png', fit: 'crop' },
  'Rail Delivery Group': { src: '/logos/national-rail.webp', fit: 'wide' },
  'DfT BODS': { src: '/logos/govuk.png', fit: 'bleed' },
  'NaPTAN (DfT)': { src: '/logos/govuk.png', fit: 'bleed' },
  'Fuel Finder (gov.uk)': { src: '/logos/govuk.png', fit: 'bleed' },
  'UK Hydrographic Office': { src: '/logos/ukho.png', fit: 'emblem' },
  // No mark yet: adsb.lol, Scottish Water. They render the dashed
  // placeholder, which is a designed state rather than a gap.
};

/** Coverage worth stating — DNOs are licensed by area, so the card says which. */
export const REGIONS: Record<string, string> = {
  SSEN: 'North of Scotland — Aberdeen, Highlands, islands',
  'SP Energy Networks': 'Central & southern Scotland — Glasgow, Edinburgh, Borders',
  'Scottish Water': 'All of Scotland',
  'Transport Scotland': 'Scottish trunk roads',
  // The other half of the roads: every council street and utility trench in
  // the country files into one register, which is what the trunk feeds are not.
  'Scottish Road Works Commissioner': 'Every road in Scotland — the register councils and utilities file into',
  SEPA: 'Scotland',
  'Spatial Hub (Improvement Service)': 'All of Scotland — the four LEZ cities',
  'Edinburgh Festivals': 'Edinburgh',
};

/**
 * Cadence copy for feeds that are baked, not polled. Their
 * expectedEverySeconds is a staleness threshold (35 days), and "refreshes
 * every 35d" would misdescribe a chore a person does monthly.
 */
export const CADENCE: Record<string, string> = {
  'bus-stops': 're-baked monthly',
  taxis: 're-baked monthly',
  // No cadence worth stating: a boundary changes when a council amends an
  // order, and the file is re-baked by hand when it does.
  lez: 'baked, updated when a zone changes',
  // Accumulated rather than fetched: the register publishes a day of changes,
  // and the box folds each one into what it already holds. See #59.
  'local-roadworks': 'built up daily from the register’s change file',
};
